import React, { useRef, useState } from "react";
import {
  addDoc,
  collection,
  serverTimestamp,
  Timestamp,
} from "@firebase/firestore";
import { useQueryClient } from "@tanstack/react-query";
import { useFirestore, useUser } from "reactfire";

import { useTranslator } from "@/i18n/useTranslator";

import { NotesQueryKeys } from "../data/queries/notesQueries";
// @ts-expect-error - Temporarily ignoring until getRelativeDate is converted to TypeScript
import getRelativeDate from "../utils/getRelativeDate";
import { DeleteDocModal } from "./DeleteDocModal";
import { ImgSliderFromPaths } from "./ImgsSliderFromPaths";
import { DsButton, DsIcon, DsText, DsTextArea } from "./ui";
import { UploadFile } from "./UploadFile";

type NoteType = {
  id: string;
  note: string;
  createdAt: Timestamp;
  createdBy: {
    id: string;
    photoURL: string;
    name: string;
  };
  files?: string[];
  jobId?: string;
  serviceId?: string;
  taskId?: string;
};

type NotesFeedPropsType = {
  notes: NoteType[];
  path: string;
  ids: {
    jobId?: string;
    serviceId?: string;
    taskId?: string;
  };
  customer?: boolean;
};

export const NotesFeed = ({
  notes,
  path,
  ids,
  customer = false,
}: NotesFeedPropsType) => {
  const firestore = useFirestore();
  const { status, data: user } = useUser();
  const newNoteRef = useRef<HTMLTextAreaElement>(null);
  const [files, setFiles] = useState<string[]>([]);
  const { t } = useTranslator();

  const queryClient = useQueryClient();

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const noteBody = newNoteRef.current?.value;
    if (!user) return;

    const note = {
      note: noteBody ?? "",
      createdAt: serverTimestamp(),
      createdBy: {
        id: user.uid,
        photoURL: user.photoURL,
        name: user.displayName,
      },
      files,
      ...ids,
    };
    const notesCollection = collection(firestore, path);
    void addDoc(notesCollection, note)
      .then(() => {
        if (newNoteRef.current) {
          newNoteRef.current.value = "";
        }
        setFiles([]);

        // Invalidate task notes query to reflect Dashboard icon
        void queryClient.invalidateQueries({ queryKey: NotesQueryKeys.all });
      })
      .catch(error => {
        console.error("Error adding note:", error);
        alert(t("notes_feed.error_adding"));
        if (newNoteRef.current) {
          newNoteRef.current.value = noteBody;
        }
      });
  };

  if (status === "loading") {
    return <DsText variant="body">{t("loading")}</DsText>;
  }

  return (
    <div>
      <section aria-labelledby="notes-title">
        <div className="bg-white sm:rounded-lg sm:overflow-hidden">
          <div className="">
            <DsText variant="pageTitle" id="notes-title" className="sr-only">
              {t("notes")}
            </DsText>
            <div className="">
              <ul role="list" className="flex flex-col gap-4 divide-y">
                {notes?.length > 0 &&
                  notes.map(note => (
                    <li key={note.id} className="pt-4 last-of-type:pb-4 px-4">
                      <div className="flex space-x-3">
                        <div className="flex-shrink-0">
                          <img
                            className="h-10 w-10 rounded-full border"
                            src={note.createdBy.photoURL}
                            alt=""
                          />
                        </div>
                        <div className="w-full flex">
                          <div>
                            <div className="text-sm flex">
                              <DsText
                                variant="cardTitle"
                                className="text-gray-900">
                                {note.createdBy.name}
                              </DsText>
                            </div>
                            <div className="mt-1">
                              <DsText
                                variant="dataDisplayValue"
                                className="text-gray-700">
                                {note.note}
                              </DsText>
                            </div>
                            {Boolean(note.files?.length) && (
                              <div className="flex gap-4 my-4 flex-wrap">
                                <ImgSliderFromPaths
                                  className="w-12 h-12 object-cover rounded shadow"
                                  paths={note.files}
                                />
                              </div>
                            )}
                            <div className="mt-2">
                              <DsText variant="body" className="text-gray-500">
                                {getRelativeDate(note.createdAt?.toDate())}
                              </DsText>
                            </div>
                          </div>
                          {!customer && (
                            <DeleteDocModal
                              buttonContent={<DsIcon icon="Close" />}
                              buttonClassName="ml-auto cursor-pointer p-2 -top-2 -right-2 relative text-gray-500"
                              path={`jobs/${note.jobId}/${
                                note.serviceId
                                  ? `services/${note.serviceId}/${
                                      note.taskId
                                        ? `tasks/${note.taskId}/notes/${note.id}`
                                        : `notes/${note.id}`
                                    }`
                                  : `notes/${note.id}`
                              }`}
                              type="note"
                            />
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          {!customer && user && (
            <>
              <div className="bg-gray-50 px-4 py-6 sm:px-6">
                <div className="flex space-x-3">
                  <div className="flex-shrink-0">
                    <img
                      className="h-10 w-10 rounded-full border"
                      src={user.photoURL ?? ""}
                      alt=""
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <form action="#">
                      <div className="mb-2">
                        <label htmlFor="comment" className="sr-only">
                          {t("notes_feed.about")}
                        </label>
                        <DsTextArea
                          ref={newNoteRef}
                          id="comment"
                          name="comment"
                          placeholder={t("notes_feed.add_note")}
                          defaultValue=""
                          size="md"
                        />
                      </div>
                      <div className="flex gap-4">
                        {files.length > 0 && (
                          <ImgSliderFromPaths
                            className="w-12 h-12 object-cover rounded shadow"
                            paths={files}
                          />
                        )}
                      </div>
                      <UploadFile
                        multiple
                        basePath={path}
                        baseName="notefile"
                        onUploadComplete={photoRef => {
                          setFiles(photoRef as string[]);
                        }}
                      />

                      <div className="mt-3 flex items-center justify-between">
                        <DsButton onClick={handleSubmit} variant="primary">
                          {t("notes_feed.add_note")}
                        </DsButton>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};
